import { useState } from "react";
import { requestProlificExit, submitConsent } from "../service/api";
import PageWrapper from "./PageWrapper";
import "./ConsentForm.css";

const EXIT_COPY = Object.freeze({
  no_consent: "You chose not to consent, so the study cannot continue.",
  incompatible_device: "This study requires a desktop or laptop, so it cannot continue on this device.",
});

const ConsentForm = ({
  userId,
  participantSource,
  onConsent,
  navigateTo = (url) => window.location.assign(url),
}) => {
  const [consent, setConsent] = useState(false);
  const [desktopConfirmed, setDesktopConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [stoppedMessage, setStoppedMessage] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!consent || !desktopConfirmed || !userId || submitting || submitted) return;

    setSubmitting(true);
    try {
      const response = await submitConsent(userId);
      if (response.data.success) {
        setSubmitted(true);
        setError("");
        localStorage.setItem("currentStep", "demographics");
        onConsent();
      }
    } catch (err) {
      console.error("Error submitting consent:", err);
      setError("Could not save your consent. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const stopStudy = async (reason) => {
    if (exiting) return;
    setExiting(true);
    setError("");
    setStoppedMessage(EXIT_COPY[reason]);

    if (participantSource !== "prolific") return;

    try {
      const result = await requestProlificExit(userId, reason);
      navigateTo(result.redirectUrl);
    } catch (err) {
      console.error("Could not open the configured Prolific return destination:", err);
      setError("Please return to Prolific and cancel or return your submission.");
    }
  };

  if (stoppedMessage) {
    return (
      <PageWrapper as="section" maxWidth="600px" className="consent-content">
        <div className="text-center" role="status">
          <h2>Study stopped</h2>
          <p>{stoppedMessage}</p>
          {participantSource === "prolific" ? (
            <p>{error || "Redirecting you to Prolific to request a return…"}</p>
          ) : (
            <p>You may now close this page.</p>
          )}
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper as="section" maxWidth="600px" className="consent-content">
      <div>
        <h2 className="consent-subtitle">Study information and consent</h2>

        <div className="consent-copy">
          <p><strong>Purpose:</strong> This study investigates movie preferences and movie-selection behaviour.</p>
          <p><strong>Time and device:</strong> The study takes approximately 40 minutes and must be completed on a desktop or laptop computer.</p>
          <p><strong>What you will do:</strong> You will answer brief background questions, select movies you like and dislike, complete a visual working-memory task, and make a series of comparisons between pairs of movies.</p>
          <p><strong>Data and privacy:</strong> We collect your study responses, movie preferences and selections, working-memory responses, movie-comparison choices, and response times. If you participate through Prolific, your Prolific participant and session identifiers are linked to an internal study ID. Your data are pseudonymised, used only for research, and accessible only to the research team. They will be retained for 5 years by the Polish-Japanese Academy of Information Technology. For questions about the study, your data, or withdrawal/deletion of your data, contact <a href="mailto:weronika.k@pjwstk.edu.pl">weronika.k@pjwstk.edu.pl</a>.</p>
          <p><strong>Voluntary participation:</strong> Taking part is voluntary. You may stop at any time without penalty. If you joined through Prolific and decide not to participate, please return or cancel your submission on Prolific.</p>

          <label className="consent-confirmation mt-lg" htmlFor="consent">
            <input
              type="checkbox"
              id="consent"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
              data-testid="consent-checkbox"
              required
            />
            <span>
              I have read the information above and agree to take part in this study.
            </span>
          </label>

          <label className="consent-confirmation mt-md" htmlFor="desktop-confirmation">
            <input
              type="checkbox"
              id="desktop-confirmation"
              checked={desktopConfirmed}
              onChange={(event) => setDesktopConfirmed(event.target.checked)}
              data-testid="desktop-checkbox"
              required
            />
            <span>
              I confirm that I am completing this study on a desktop or laptop computer.
            </span>
          </label>
        </div>

        {error && <div className="alert alert-error mt-md" role="alert">{error}</div>}

        <div className="consent-actions mt-lg">
          <button
            className="btn btn-success"
            onClick={handleSubmit}
            disabled={submitted || submitting || exiting || !consent || !desktopConfirmed}
            data-testid="consent-submit"
          >
            I consent and begin the study
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => stopStudy("no_consent")}
            disabled={submitted || exiting}
          >
            I do not consent
          </button>
        </div>

        <div className="consent-device-exit mt-md">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => stopStudy("incompatible_device")}
            disabled={submitted || exiting}
          >
            I am not using a desktop or laptop
          </button>
        </div>
      </div>
    </PageWrapper>
  );
};

export default ConsentForm;
